import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus, Search, Pencil, Trash2, X, CheckCircle2, CircleSlash2,
  Loader2, ArrowUpDown, FileText, Save, Send, Info, ShieldCheck
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import MyContext from "../../../MyContext";
import "./CreditStyle.css";
import loader from "../../../images/Anlook.svg";
import CustomerInvoiceImageModal from "./CustomerInvoiceImageModal";

export default function CustomerUdhaariTable({ userID }) {
  const { baseUrl, config } = useContext(MyContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [sortKey, setSortKey] = useState("customerId");
  const [sortDir, setSortDir] = useState("asc");

  // ====== BILL MODAL STATE ======
  const [billOpen, setBillOpen] = useState(false);
  const [billRow, setBillRow] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [billLoading, setBillLoading] = useState(false);
  const [billUrl, setBillUrl] = useState("");

  // modal fields (prefill-aware)
  const [billingName, setBillingName] = useState("");
  const [billingGst, setBillingGst] = useState("");
  const [isOldBill, setIsOldBill] = useState("No"); // default "No"
  const [billRemarks, setBillRemarks] = useState('')

  // Bill type: radio (auto/custom)
  const [billType, setBillType] = useState("auto"); // "auto" | "custom"
  const [billAmount, setBillAmount] = useState("");
  const [generatedBillDetails, setGeneratedBillDetails] = useState({})
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceRow, setInvoiceRow] = useState(null);
  function openInvoice(row) {
    setInvoiceRow(row);
    setInvoiceOpen(true);
  }



  // ---- Helpers
  const to2 = (v) => {
    const n = parseFloat(v ?? 0);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
  };

  // Map API row -> UI row (includes new billing_name & billing_gst)
  const mapApiToUi = (r) => ({
    customerId: String(r?.pk_customer_id ?? r?.customerId ?? ""),
    userid: String(r?.fk_user_id ?? r?.userid ?? ""),
    customerMobile: String(r?.customer_mobile ?? r?.customerMobile ?? ""),
    customerName: String(r?.customer_name ?? r?.customerName ?? ""),
    loyaltyPoints: String(r?.loyalty_points ?? r?.loyaltyPoints ?? "0"),
    creditLimit: to2(r?.creditLimit ?? r?.credit ?? 0),
    dueAmount: to2(r?.due_amount ?? 0),
    createdOn: String(r?.created_on ?? r?.createdOn ?? ""),
    creditAllowed: (r?.credit_allowed ?? r?.creditAllowed) === "Y" ? "Y" : "N",
    billingName: r?.billing_name ?? null, // NEW
    billingGst: r?.billing_gst ?? null,   // NEW
  });

  // ---- APIs
  const addOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/addOrderCustomerAccountDetails`, payload, config);

  const getAllOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/getAllOrderCustomerAccountDetails`, payload, config);

  const updateOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/updateOrderCustomerAccountDetails`, payload, config);

  const deleteOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/deleteOrderCustomerAccountDetails`, payload, config);

  // generate bill includes billingGst (unchanged from last step)
  const generateCreditBill = ({ userid, mobileNumber, billingName, billingGst, isOldBill, fromDate, toDate, billAmount, remarks }) =>
    axios.post(
      `${baseUrl}/api/Order/v2.0/generateCreditBill`,
      { userid, mobileNumber, billingName, billingGst, isOldBill, fromDate, toDate, billAmount, remarks },
      config
    );

  // settle bill includes isOldBill + billAmount + invoiceLink
  const settleCreditBill = ({ userid, mobileNumber, isOldBill, fromDate, toDate, billAmount, invoiceLink }) =>
    axios.post(
      `${baseUrl}/api/Order/v1.0/settleCreditBill`,
      { userid, mobileNumber, isOldBill, fromDate, toDate, billAmount, invoiceLink },
      config
    );

  // save invoice API (updated payload previously)
  const saveInvoice = ({ userid, mobileNumber, billingName, billAmount, invoiceLink }) =>
    axios.post(
      `${baseUrl}/api/Order/Invoice/v1.0/saveInvoice`,
      { userid, mobileNumber, billingName, billAmount, invoiceLink },
      config
    );

  // ---- Load
  async function reload() {
    if (!userID) { toast.error("Missing userid"); return; }
    try {
      setLoading(true);
      const res = await getAllOrderCustomerAccountDetails({ userid: userID });
      const ok = res?.data?.responseCode === "00";
      const list = ok ? res?.data?.response : res?.data || [];
      const mapped = Array.isArray(list) ? list.map(mapApiToUi) : [];
      setRows(mapped);
      if (!ok) toast.error(res?.data?.responseMessage || "Failed to fetch");
    } catch {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userID]);

  // ---- Filter + Sort
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(
        (r) =>
          String(r.customerMobile || "").toLowerCase().includes(q) ||
          String(r.customerId || "").toLowerCase().includes(q) ||
          String(r.customerName || "").toLowerCase().includes(q)
      )
      : rows.slice();

    base.sort((a, b) => {
      const A = String(a[sortKey] ?? "");
      const B = String(b[sortKey] ?? "");
      return sortDir === "asc" ? A.localeCompare(B, "en", { numeric: true }) : B.localeCompare(A, "en", { numeric: true });
    });
    return base;
  }, [rows, search, sortKey, sortDir]);

  function cycleSort(key) {
    setSortKey(key);
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  }

  // ---- Drawer (unchanged)
  const [form, setForm] = useState({
    customerName: "",
    customerMobile: "",
    loyaltyPoints: "",
    creditAllowed: "N",
    creditLimit: "",
    remarks: "",
  });

  function openAdd() {
    setEditing(null);
    setForm({ customerName: "", customerMobile: "", loyaltyPoints: "", creditAllowed: "N", creditLimit: "", remarks: "" });
    setDrawerOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      customerName: row?.customerName || "",
      customerMobile: row?.customerMobile || "",
      loyaltyPoints: row?.loyaltyPoints ?? "",
      creditAllowed: row?.creditAllowed === "Y" ? "Y" : "N",
      creditLimit: row?.creditLimit ?? "",
      remarks: "",
    });
    setDrawerOpen(true);
  }
  function closeDrawer() {
    if (!submitting) {
      setDrawerOpen(false);
      setEditing(null);
    }
  }
  function validate() {
    if (!editing) {
      if (!(form.customerName || "").trim()) return "Enter customer name";
      const mobile = (form.customerMobile || "").trim();
      if (!/^[6-9]\d{9}$/.test(mobile)) return "Enter valid 10-digit Indian mobile";
    }
    if (form.loyaltyPoints !== "" && !/^\d+(\.\d+)?$/.test(form.loyaltyPoints)) return "Loyalty Points must be a number";
    if (form.creditLimit !== "" && !/^\d+(\.\d+)?$/.test(form.creditLimit)) return "Credit Limit must be a number";
    if (!["Y", "N"].includes(form.creditAllowed)) return "Credit Allowed must be Y or N";
    return null;
  }
  async function handleSubmit(e) {
    e?.preventDefault?.();
    const err = validate(); if (err) return toast.warning(err);
    try {
      setSubmitting(true);
      if (editing) {
        const res = await updateOrderCustomerAccountDetails({
          customerId: editing.customerId,
          loyaltyPoints: String(form.loyaltyPoints ?? ""),
          creditAllowed: form.creditAllowed,
          creditLimit: String(form.creditLimit ?? ""),
        });
        if (res?.data?.responseCode === "00") { toast.success(res?.data?.responseMessage || "Customer updated"); closeDrawer(); reload(); }
        else toast.error(res?.data?.responseMessage || "Update failed");
      } else {
        const res = await addOrderCustomerAccountDetails({
          userid: userID,
          customerName: String(form.customerName ?? ""),
          customerMobile: String(form.customerMobile ?? ""),
          loyaltyPoints: String(form.loyaltyPoints ?? ""),
          creditAllowed: form.creditAllowed,
          creditLimit: String(form.creditLimit ?? ""),
          remarks: String(form.remarks ?? ""),
        });
        if (res?.data?.responseCode === "00") { toast.success(res?.data?.responseMessage || "Customer added"); closeDrawer(); reload(); }
        else toast.error(res?.data?.responseMessage || "Add failed");
      }
    } catch {
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Toggle Credit
  async function toggleCreditAllowed(row) {
    const next = row?.creditAllowed === "Y" ? "N" : "Y";
    const old = row?.creditAllowed;
    setRows((prev) => prev.map((it) => (it.customerId === row.customerId ? { ...it, creditAllowed: next } : it)));
    try {
      const res = await updateOrderCustomerAccountDetails({
        customerId: row.customerId,
        loyaltyPoints: String(row.loyaltyPoints ?? ""),
        creditAllowed: next,
        creditLimit: String(row.creditLimit ?? ""),
      });
      if (res?.data?.responseCode === "00") toast.success(`Credit ${next === "Y" ? "enabled" : "disabled"}`);
      else throw new Error(res?.data?.responseMessage || "Toggle failed");
    } catch {
      toast.error("Toggle failed");
      setRows((prev) => prev.map((it) => (it.customerId === row.customerId ? { ...it, creditAllowed: old } : it)));
    }
  }

  // ---- Delete
  async function handleDelete() {
    if (!deleting?.customerId) return;
    try {
      setSubmitting(true);
      const res = await deleteOrderCustomerAccountDetails({ customerId: deleting.customerId });
      if (res?.data?.responseCode === "00") { toast.success(res?.data?.responseMessage || "Customer deleted"); setDeleting(null); reload(); }
      else toast.error(res?.data?.responseMessage || "Delete failed");
    } catch {
      toast.error("Delete failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ====== BILL MODAL handlers ======
  function openBill(row) {
    setBillRow(row);
    setFromDate(today);
    setToDate(today);
    setBillType("auto");
    setBillAmount("");
    // PREFILL from API: prefer billingName/billingGst; fallback to customerName; GST blank if null
    setBillingName((row?.billingName && String(row.billingName).trim()) ? row.billingName : (row?.customerName || ""));
    setBillingGst(row?.billingGst ? String(row.billingGst).toUpperCase() : "");
    setIsOldBill("No");
    setBillUrl("");
    setBillOpen(true);
  }
  function closeBill() {
    if (!billLoading) setBillOpen(false);
  }

  function validateAmount(x) {
    if (!/^\d+(\.\d{1,2})?$/.test(String(x))) return false;
    return Number(x) >= 0;
  }

  async function onFetchBill() {
    if (!userID || !billRow?.customerMobile) return toast.error("Missing userid or mobile");
    if (!(billingName || "").trim()) return toast.warning("Enter Billing Name before fetching the bill");

    const useCustom = billType === "custom";
    const amt = useCustom ? (billAmount || "").trim() : "0";
    if (useCustom) {
      if (!amt) return toast.warning("Enter bill amount");
      if (!validateAmount(amt)) return toast.warning("Enter a valid amount (max 2 decimals)");
    }

    try {
      setBillLoading(true);
      const payload = {
        userid: String(userID),
        mobileNumber: String(billRow.customerMobile),
        billingName: String(billingName).trim(),
        billingGst: String(billingGst || "").toUpperCase(),
        isOldBill: String(isOldBill), // "Yes" | "No"
        fromDate: String(fromDate),
        toDate: String(toDate),
        billAmount: String(amt),
        remarks: billRemarks,
      };
      const res = await generateCreditBill(payload);

      const rawUrl =
        res?.data?.image_url ||
        // res?.data?.data?.image_url ||
        // res?.data?.response?.image_url ||
        "";



      if (!rawUrl) {
        setBillUrl("");
        toast.error(res.data.responseMessage);
        return;
      }
      const finalUrl = rawUrl.includes("?") ? `${rawUrl}&_=${Date.now()}` : `${rawUrl}?_=${Date.now()}`;
      setBillUrl(finalUrl);
      setGeneratedBillDetails(res.data.response)
      toast.success(res.data.responseMessage);
    } catch {
      setBillUrl("");
      toast.error("failed !  Error");
    } finally {
      setBillLoading(false);
    }
  }

  async function onSendToCustomer() {
    if (!userID || !billRow?.customerMobile) return toast.error("Missing userid or mobile");
    if (!billUrl) return toast.warning("Please fetch the bill first");
    // if (isOldBill !== "Yes") return toast.info('Enable "Old Bill = Yes" to send to customer');

    const useCustom = billType === "custom";
    const amtToSend = useCustom ? (billAmount || "0") : "0";

    try {
      setBillLoading(true);
      const payload = {
        userid: String(userID),
        mobileNumber: String(billRow.customerMobile),
        isOldBill: String(isOldBill),
        fromDate: String(fromDate),
        toDate: String(toDate),
        billAmount: String(amtToSend),
        invoiceLink: String(billUrl),
      };
      const res = await settleCreditBill(payload);
      if (res?.data?.responseCode === "00") toast.success(res?.data?.responseMessage || "Bill sent to customer");
      else toast.error(res?.data?.responseMessage || "Failed to send bill");
    } catch {
      toast.error("Failed to send bill");
    } finally {
      setBillLoading(false);
    }
  }

  // Save Invoice (persists but doesn't send) — uses new payload
  async function onSaveInvoice() {
    if (!userID || !billRow?.customerMobile) return toast.error("Missing userid or mobile");
    if (!billUrl) return toast.warning("Please fetch the bill first");
    if (!(billingName || "").trim()) return toast.warning("Enter Billing Name before saving");

    const useCustom = billType === "custom";
    const amtToSave = useCustom ? (billAmount || "0") : "0";

    try {
      setBillLoading(true);
      const payload = {
        userid: String(userID),
        mobileNumber: String(billRow.customerMobile),
        billingName: String(billingName).trim(),
        billAmount: amtToSave === "0" || !amtToSave ? generatedBillDetails.item.totalOrderPrice : amtToSave,
        invoiceLink: String(billUrl),
      };
      const res = await saveInvoice(payload);
      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Invoice saved");
      } else {
        toast.error(res?.data?.responseMessage || "Failed to save invoice");
      }
    } catch {
      toast.error("Failed to save invoice");
    } finally {
      setBillLoading(false);
    }
  }

  return (
    <div className="admin-credit-block">
      <ToastContainer position="top-right" theme="colored" closeOnClick pauseOnHover={false} />

      {/* Toolbar */}
      <div className="admin-credit-toolbar">
        <div className="admin-input with-icon">
          <Search size={16} className="admin-input-icon" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, mobile or ID..." />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={openAdd} className="admin-btn admin-btn-success"><Plus size={18} /> Add Customer</button>
          <span className="admin-badge">Total: {rows.length}</span>
          {loading && <span className="admin-badge muted">Loading…</span>}
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        {loading ? (
          <div className=" m-5 p-5 d-flex justify-content-center">
            <div className=" d-flex flex-column align-items-center justify-content-center ">
              <div className="p-3"><div className="loader"> </div></div>
              <div className="p-3"><img src={loader} alt="" height={40} /></div>
            </div>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => cycleSort("customerId")}><ArrowUpDown size={14} /> ID</th>
                <th onClick={() => cycleSort("customerMobile")}><ArrowUpDown size={14} /> Mobile</th>
                <th>Name</th>
                <th>Credit Limit</th>
                <th>Due Amount</th>
                <th>Created on</th>
                <th>Status</th>
                <th style={{ width: 210 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="center muted">No customers found</td></tr>
              ) : filtered.map((r) => {
                const creditOn = r.creditAllowed === "Y";
                return (
                  <tr key={r.customerId || r.customerMobile}>
                    <td className="mono">{r.customerId || "—"}</td>
                    <td className="mono">{r.customerMobile || "—"}</td>
                    <td>{r.customerName || "—"}</td>
                    <td>₹ {to2(r.creditLimit)}</td>
                    <td>₹ {to2(r.dueAmount)}</td>
                    <td>{r.createdOn || "—"}</td>
                    <td>
                      <span className={`admin-chip ${creditOn ? "yes" : "no"}`}>
                        {creditOn ? <CheckCircle2 size={14} /> : <CircleSlash2 size={14} />}
                        {creditOn ? "Credit ON" : "Credit OFF"}
                      </span>
                    </td>
                    <td className="row-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="admin-icon-btn" title="Toggle Credit" onClick={() => toggleCreditAllowed(r)}>
                        {creditOn ? <CheckCircle2 size={18} /> : <CircleSlash2 size={18} />}
                      </button>
                      <button className="admin-icon-btn" title="Edit" onClick={() => openEdit(r)}>
                        <Pencil size={18} />
                      </button>
                      <button className="admin-btn admin-btn-neutral btn--sm" onClick={() => openBill(r)}>
                        <FileText size={16} style={{ marginRight: 6 }} /> Bill
                      </button>
                      {/* <button
                        className="admin-btn admin-btn-neutral btn--sm"
                        onClick={() => openInvoice(r)}
                      >
                        <FileText size={16} style={{ marginRight: 6 }} /> Invoice
                      </button> */}

                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <div className="admin-drawer-backdrop">
          <div className="admin-drawer admin-drawer-compact" onClick={(e) => e.stopPropagation()}>
            <div className="admin-drawer-header">
              <h4 className="admin-drawer-title">{editing ? "Edit Customer" : "Add Customer"}</h4>
              <button className="admin-icon-btn" onClick={closeDrawer}><X size={18} /></button>
            </div>

            <form className="admin-drawer-body admin-drawer-form" onSubmit={handleSubmit}>
              {!editing && (
                <>
                  <div className="admin-field">
                    <label>Customer Name <span className="req">*</span></label>
                    <input
                      className="admin-input-control"
                      placeholder="Full name"
                      value={form.customerName}
                      onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                    />
                  </div>
                  <div className="admin-field">
                    <label>Remark</label>
                    <input
                      className="admin-input-control"
                      placeholder="Add Remark"
                      value={form.remarks}
                      onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                    />
                  </div>
                  <div className="admin-field">
                    <label>Customer Mobile <span className="req">*</span></label>
                    <input
                      className="admin-input-control"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit mobile"
                      value={form.customerMobile}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, customerMobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                      }
                    />
                  </div>
                </>
              )}

              <div className="admin-field">
                <label>Loyalty Points</label>
                <input
                  className="admin-input-control"
                  inputMode="decimal"
                  placeholder="e.g., 120"
                  value={form.loyaltyPoints}
                  onChange={(e) => setForm((f) => ({ ...f, loyaltyPoints: e.target.value }))}
                />
              </div>

              <div className="admin-field">
                <label>Credit Allowed</label>
                <div className="admin-toggle admin-toggle-pill">
                  <button type="button" className={`admin-toggle-btn ${form.creditAllowed === "Y" ? "active" : ""}`} onClick={() => setForm((f) => ({ ...f, creditAllowed: "Y" }))}>Yes</button>
                  <button type="button" className={`admin-toggle-btn ${form.creditAllowed === "N" ? "active" : ""}`} onClick={() => setForm((f) => ({ ...f, creditAllowed: "N" }))}>No</button>
                </div>
              </div>

              <div className="admin-field">
                <label>Credit Limit (₹)</label>
                <input
                  className="admin-input-control"
                  inputMode="decimal"
                  placeholder="e.g., 5000"
                  value={form.creditLimit}
                  onChange={(e) => setForm((f) => ({ ...f, creditLimit: e.target.value }))}
                />
              </div>

              <div className="admin-drawer-footer">
                <button type="button" className="admin-btn" onClick={closeDrawer}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-success" disabled={submitting}>
                  {submitting ? <Loader2 className="admin-spin" size={16} /> : null}
                  {editing ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleting && (
        <div className="admin-modal-backdrop" onClick={() => setDeleting(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h4>Delete Customer</h4>
              <button className="admin-icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button>
            </div>
            <div className="admin-confirm">
              <p>Delete <b>{deleting.customerMobile}</b>? This cannot be undone.</p>
              <div className="admin-modal-actions">
                <button className="admin-btn" onClick={() => setDeleting(null)}>Cancel</button>
                <button className="admin-btn admin-btn-danger" disabled={submitting} onClick={handleDelete}>
                  {submitting ? <Loader2 className="admin-spin" size={16} /> : null}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====== Generate Credit Bill Modal ====== */}
      {billOpen && (
        <div className="admin-modal-backdrop" onClick={closeBill}>
          <div
            className="admin-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "95%",
              maxWidth: 860,
              display: "flex",
              flexDirection: "column",
              maxHeight: "88vh",
            }}
          >
            {/* Local styles for professional layout */}
            <style>{`
              .grid-2 { display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
              .grid-3 { display:grid; grid-template-columns: 1fr 1fr 1fr; gap:8px; }
              .grid-1 { display:grid; grid-template-columns: 1fr; gap:12px; }
              .note { font-size:12px; color:#6b7280; display:flex; align-items:center; gap:6px; }
              .card-radio {
                border:1px solid #e5e7eb; border-radius:12px; padding:12px; background:#fff;
                display:flex; gap:12px; align-items:flex-start;
              }
              .card-radio input[type="radio"] { margin-top:2px; }
              .chip {
                display:inline-flex; align-items:center; gap:6px; font-size:12px;
                padding:2px 8px; border-radius:9999px; background:#f3f4f6; color:#374151;
              }
              .footer-cta .btn {
                height:40px; display:inline-flex; align-items:center; justify-content:center; gap:8px;
                border:none; border-radius:10px; font-weight:600; color:#fff; width:100%;
                box-shadow:0 1px 1px rgba(0,0,0,.04);
                transition:transform .12s ease, box-shadow .12s ease, opacity .12s ease;
              }
              .footer-cta .btn:hover { transform:translateY(-0.5px); box-shadow:0 2px 6px rgba(0,0,0,.08); }
              .btn-red { background:#ef4444; }
              .btn-red:hover { background:#dc2626; }
              .btn-blue { background:#2563eb; }
              .btn-blue:hover { background:#1d4ed8; }
              .btn-green { background:#01C37D; }
              .btn-green:hover { background:#00b06f; }
              .pill-group { display:flex; gap:16px; flex-wrap:wrap; }
              .radio-row { display:flex; align-items:center; gap:8px; }
              .muted { color:#6b7280; }
            `}</style>

            <div className="admin-modal-header">
              <h4>Generate Credit Bill</h4>
              <button className="admin-icon-btn" onClick={closeBill}><X size={18} /></button>
            </div>

            {/* Body */}
            <div className="admin-drawer-body admin-drawer-form" style={{ overflowY: "auto" }}>
              <div className="grid-1">
                <div className="grid-2">
                  <div>
                    <label>Mobile</label>
                    <div className="admin-input-control mono" style={{ opacity: 0.9 }}>
                      {billRow?.customerMobile || "—"}
                    </div>
                  </div>
                  <div>
                    <label>Billing Name <span className="req">*</span></label>
                    <input
                      className="admin-input-control"
                      placeholder="Enter billing name"
                      value={billingName}
                      onChange={(e) => setBillingName(e.target.value)}
                    />
                  </div>
                </div>

                {/* GSTIN (prefilled if available) */}
                <div className="grid-2">
                  <div>
                    <label>Billing GSTIN (optional)</label>
                    <input
                      className="admin-input-control mono"
                      placeholder="22AAAAA0000A1Z5"
                      value={billingGst}
                      onChange={(e) => setBillingGst(e.target.value.toUpperCase())}
                      maxLength={15}
                    />
                    <div className="note" style={{ marginTop: 6 }}>
                      <Info size={14} /> Prefilled from customer, editable. Leave blank if not applicable.
                    </div>
                  </div>
                  <div>
                    <label>Remarks (optional)</label>
                    <textarea
                      className="admin-input-control mono"
                      placeholder="text"
                      value={billRemarks}
                      onChange={(e) => setBillRemarks(e.target.value)}
                      // maxLength={15}
                      type="textarea"

                    />
                    <div className="note" style={{ marginTop: 6 }}>
                      <Info size={14} /> Prefilled from customer, editable. Leave blank if not applicable.
                    </div>
                  </div>
                  <div />
                </div>

                {/* Old Bill – card radios */}
                <div className="grid-2">
                  <div className="card-radio">
                    <input
                      type="radio"
                      id="oldNo"
                      name="oldBill"
                      value="No"
                      checked={isOldBill === "No"}
                      onChange={() => setIsOldBill("No")}
                    />
                    <label htmlFor="oldNo" style={{ width: "100%" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <strong>New Bill</strong>
                        <span className="chip"><Info size={14} /> Default</span>
                      </div>
                      <div className="muted">Generate a fresh credit bill. “Send to customer” stays disabled.</div>
                    </label>
                  </div>

                  <div className="card-radio">
                    <input
                      type="radio"
                      id="oldYes"
                      name="oldBill"
                      value="Yes"
                      checked={isOldBill === "Yes"}
                      onChange={() => setIsOldBill("Yes")}
                    />
                    <label htmlFor="oldYes" style={{ width: "100%" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <strong>Old Bill</strong>
                        <span className="chip" style={{ background: "#ecfdf5", color: "#065f46" }}>
                          <ShieldCheck size={14} /> Allowed to send
                        </span>
                      </div>
                      <div className="muted">Enable this only if you’re sending a previously pending/old bill.</div>
                    </label>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid-2">
                  <div>
                    <label>From Date</label>
                    <input type="date" className="admin-input-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div>
                    <label>To Date</label>
                    <input type="date" className="admin-input-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                </div>

                {/* Bill Type – radio */}
                <div className="grid-2">
                  <div>
                    <label>Bill Type</label>
                    <div className="pill-group" role="radiogroup" aria-label="Bill Type">
                      <label className="radio-row">
                        <input
                          type="radio"
                          name="billType"
                          value="auto"
                          checked={billType === "auto"}
                          onChange={() => setBillType("auto")}
                        />
                        <span>Auto Bill (₹0)</span>
                      </label>
                      <label className="radio-row">
                        <input
                          type="radio"
                          name="billType"
                          value="custom"
                          checked={billType === "custom"}
                          onChange={() => setBillType("custom")}
                        />
                        <span>Custom Bill</span>
                      </label>
                    </div>
                    <div className="note" style={{ marginTop: 6 }}>
                      <Info size={14} /> Auto mode sets amount to ₹0; choose Custom to specify an amount.
                    </div>
                  </div>

                  <div>
                    <label>Bill Amount (₹)</label>
                    <input
                      className="admin-input-control mono"
                      inputMode="decimal"
                      placeholder={billType === "custom" ? "e.g., 1500 or 1500.50" : "Disabled in Auto Bill"}
                      value={billAmount}
                      onChange={(e) =>
                        setBillAmount(
                          e.target.value
                            .replace(/[^0-9.]/g, "")
                            .replace(/(\..*)\./g, "$1")
                            .slice(0, 12)
                        )
                      }
                      disabled={billType !== "custom"}
                    />
                  </div>
                </div>

                {/* Fetch CTA */}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button className="admin-btn admin-btn-primary" onClick={onFetchBill} disabled={billLoading}>
                    {billLoading ? <Loader2 className="admin-spin" size={16} /> : <FileText size={16} />}
                    <span style={{ marginLeft: 6 }}>{billLoading ? "Fetching…" : "Fetch Bill"}</span>
                  </button>
                </div>

                {/* Preview */}
                {billUrl && (
                  <div>
                    <label>Preview</label>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 8,
                        border: "1px solid #eee",
                        borderRadius: 12,
                        background: "#fafafa",
                      }}
                    >
                      <img
                        src={billUrl}
                        alt="Credit Bill"
                        style={{ width: "100%", maxWidth: 560, height: "auto", objectFit: "contain", borderRadius: 8 }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer: equal-width CTAs with red/blue/green */}
            <div className="admin-modal-actions footer-cta mt-2">
              <div className="grid-3" style={{ width: "100%" }}>
                <button className="btn btn-red" onClick={closeBill} disabled={billLoading}>
                  <X size={16} /> Close
                </button>
                <button
                  className="btn btn-blue"
                  onClick={onSaveInvoice}
                  disabled={billLoading || !billRow?.customerMobile || !billUrl || !(billingName || "").trim()}
                  title={
                    !billUrl
                      ? "Fetch bill first"
                      : !(billingName || "").trim()
                        ? "Enter Billing Name"
                        : "Save invoice"
                  }
                >
                  {billLoading ? <Loader2 className="admin-spin" size={16} /> : <Save size={16} />}
                  Save Invoice
                </button>
                <button
                  className="btn btn-green"
                  onClick={onSendToCustomer}
                  disabled={billLoading || !billRow?.customerMobile || !billUrl}
                  title={
                    !billUrl
                      ? "Fetch bill first"
                      : isOldBill !== "Yes"
                        ? 'Enable "Old Bill = Yes" to send'
                        : "Send bill to customer"
                  }
                >
                  {billLoading ? <Loader2 className="admin-spin" size={16} /> : <Send size={16} />}
                  Send to customer
                </button>
              </div>
              {/* <div className="note" style={{ marginTop: 6 }}>
                <Info size={14}/> Send to customer is available only for Old Bill.
              </div> */}
            </div>
          </div>
        </div>
      )}
      <CustomerInvoiceImageModal
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        customer={invoiceRow}
      />

    </div>
  );
}
